import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { EvertimeGpRow } from '../../services/apis/dashboard/dashboard.service';
import { chartColors, chartPalette } from './chart-theme';

export interface EvertimeGpBarChartEvent {
  dataIndex: number;
  row: EvertimeGpRow;
  nativeEvent: MouseEvent;
}

interface GpChartRow extends EvertimeGpRow {
  displayName: string;
  color: string;
}

@Component({
  selector: 'app-evertime-gp-bar-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './evertime-gp-bar-chart.component.html',
})
export class EvertimeGpBarChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() rows: EvertimeGpRow[] = [];
  @Input() tone: 'primary' | 'accent' = 'primary';
  @Input() selectedName: string | null = null;
  @Output() barClick = new EventEmitter<EvertimeGpBarChartEvent>();
  @Output() barContextMenu = new EventEmitter<EvertimeGpBarChartEvent>();

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  options: EChartsOption | null = null;

  private chartRows: GpChartRow[] = [];
  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }

    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  onChartClick(params: any): void {
    const row = this.rowFor(params?.dataIndex);
    const nativeEvent = params?.event?.event ?? params?.event;

    if (!row || !nativeEvent) {
      return;
    }

    this.barClick.emit({ dataIndex: params.dataIndex, row, nativeEvent });
  }

  onChartContextMenu(params: any): void {
    const row = this.rowFor(params?.dataIndex);
    const nativeEvent = params?.event?.event ?? params?.event;

    if (!row || !nativeEvent) {
      return;
    }

    nativeEvent.preventDefault?.();
    this.barContextMenu.emit({ dataIndex: params.dataIndex, row, nativeEvent });
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;

    const normalizedRows = (this.rows ?? [])
      .map(row => ({
        ...row,
        grossProfit: Number(row.grossProfit) || 0,
        revenue: Number(row.revenue) || 0,
      }))
      .sort((a, b) => b.grossProfit - a.grossProfit)
      .slice(0, 10);

    const c = chartColors();
    const palette = this.uniqueBarPalette();
    const nameCounts = new Map<string, number>();

    normalizedRows.forEach(row => {
      const name = (row.name ?? '').trim() || 'Unknown';
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    });

    const rows: GpChartRow[] = normalizedRows.map((row, index) => {
      const baseName = (row.name ?? '').trim() || 'Unknown';
      const hasDuplicateName = (nameCounts.get(baseName) ?? 0) > 1;
      const suffix = row.currencyCode ? ` (${row.currencyCode})` : ` #${index + 1}`;

      return {
        ...row,
        displayName: hasDuplicateName ? `${baseName}${suffix}` : baseName,
        color: palette[index % palette.length] ?? c.primary,
      };
    });

    this.chartRows = rows;

    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const valueFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
    const formatAmount = (value: number, currencyCode: string | null | undefined) => {
      const currency = (currencyCode ?? '').trim();

      if (currency) {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(value);
      }

      return valueFormatter.format(value);
    };

    this.options = {
      grid: { left: 24, right: 28, top: 16, bottom: 26, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: c.primaryBg, opacity: 0.28 } },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params;
          const row = rows[point?.dataIndex];

          if (!row) {
            return '';
          }

          return `<div style="font-family:Inter, sans-serif; min-width:170px;">
            <div style="font-weight:800; color:${c.textPrimary}; margin-bottom:8px;">${row.displayName}</div>
            <div style="display:flex; justify-content:space-between; gap:16px; margin-bottom:4px;">
              <span style="color:${c.textSecondary};">Gross Profit</span>
              <strong style="color:${c.textPrimary};">${formatAmount(row.grossProfit, row.currencyCode)}</strong>
            </div>
          </div>`;
        },
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          formatter: (value: number) => {
            if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
            if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
            return valueFormatter.format(value);
          },
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.55 } },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: rows.map(row => row.displayName),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          fontWeight: 600,
          width: 150,
          overflow: 'truncate',
          margin: 10,
        },
      },
      series: [{
        name: 'Gross Profit',
        type: 'bar',
        data: rows.map(row => ({
          value: row.grossProfit,
          itemStyle: {
            color: row.color,
            opacity: this.selectedName === null || this.selectedName === row.name ? 0.92 : 0.35,
            borderColor: this.selectedName === row.name ? c.textPrimary : 'transparent',
            borderWidth: this.selectedName === row.name ? 2 : 0,
            borderRadius: [0, 6, 6, 0],
          },
        })),
        barWidth: 18,
        cursor: 'pointer',
        emphasis: {
          focus: 'series',
          itemStyle: {
            opacity: 1,
            shadowBlur: 8,
            shadowColor: c.cardBorder,
          },
        },
      }],
    };
  }

  private rowFor(index: unknown): EvertimeGpRow | null {
    return typeof index === 'number' ? this.chartRows[index] ?? null : null;
  }

  private uniqueBarPalette(): string[] {
    const c = chartColors();
    const palette = chartPalette();
    const colors = [
      ...palette,
      c.primary,
      c.accent,
      c.chartSecondary,
      c.warning,
      c.positive,
      c.negative,
    ].filter(Boolean);

    return Array.from(new Set(colors));
  }
}
